import type { MouseEvent, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * One turn of a session transcript as markup: the question the user asked,
 * then the column the run's output fills. TaskRunTranscriptItem renders
 * every live turn through these; they hold no state and read no data, so a
 * fixture turn renders identically wherever the product's CSS is loaded.
 */
export function TranscriptTurnView(props: {
  anchorId?: string;
  selected?: boolean;
  onSelect?: () => void;
  /** The user's side of the turn, usually a UserMessageTurn. */
  user: ReactNode;
  /** The run's side: live updates, the answer, provenance and artifacts. */
  children: ReactNode;
}) {
  return (
    <section
      data-run-transcript-id={props.anchorId}
      data-run-transcript-selected={props.selected ? "true" : undefined}
      className={cn(
        "flex cursor-default flex-col gap-4 rounded-lg border border-transparent px-1 py-1.5 transition-colors sm:px-2",
        props.onSelect ? "hover:bg-background/40" : "",
        props.selected ? "bg-background/70" : "",
      )}
      onClick={props.onSelect}
      aria-current={props.selected ? "true" : undefined}
    >
      {props.user}

      <div className="flex flex-col gap-3.5">{props.children}</div>
    </section>
  );
}

/**
 * The user's message: the files it carried, a right-aligned bubble with its
 * text (none when the message was only files), then its time and author.
 */
export function UserMessageTurn(props: {
  fullWidth?: boolean;
  /** ISO timestamp for the time element. */
  dateTime: string;
  /** Full date and time, shown on hover. */
  dateTitle: string;
  /** Relative time printed beside the author, e.g. "2 mins ago". */
  timeLabel: string;
  /** Who started the turn, usually a ProvenanceAvatarStack. */
  provenance: ReactNode;
  /** Revealed on hover, usually the copy button. */
  actions?: ReactNode;
  /** The files attached to the message, above its bubble (AttachmentCardRow). */
  attachments?: ReactNode;
  onBubbleClick?: (event: MouseEvent) => void;
  /** The message, rendered as Markdown; no bubble when it is empty. */
  children?: ReactNode;
}) {
  return (
    <div className="group/user-message flex w-full items-start justify-end">
      <div
        className={cn(
          "flex min-w-0 max-w-[90%] flex-col items-end gap-1.5",
          props.fullWidth && "w-full max-w-none",
        )}
      >
        {props.attachments}
        {props.children ? (
          <div
            className={cn(
              "task-chat-bubble-user max-w-none",
              props.fullWidth && "w-full",
            )}
          >
            <div onClick={props.onBubbleClick}>{props.children}</div>
          </div>
        ) : null}
        <div className="flex shrink-0 items-center gap-1">
          <time
            dateTime={props.dateTime}
            title={props.dateTitle}
            className="whitespace-nowrap text-xs tabular-nums text-muted-foreground pointer-fine:opacity-0 sm:transition-opacity sm:group-hover/user-message:opacity-100 sm:group-focus-within/user-message:opacity-100"
          >
            {props.timeLabel}
          </time>
          {props.provenance}
          <div className="pointer-fine:opacity-0 sm:transition-opacity sm:group-hover/user-message:opacity-100 sm:group-focus-within/user-message:opacity-100">
            {props.actions}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The column an answer is set in, capped at the chat measure. */
export function AssistantMessageColumn(props: {
  venus?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className="group/assistant-response flex max-w-[720px] flex-col gap-2.5"
      data-venus-message={props.venus ? "true" : undefined}
    >
      {props.children}
    </div>
  );
}

/** Beneath a finished answer: who retrieved it and the sources it used. */
export function RunProvenanceRow(props: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{props.children}</div>;
}
